import { useCallback } from 'react'
import { config } from '@/lib/config.runtime'
import { useStubAuth, useEntraAuth } from '@/hooks/useAuth'
import type { AuthState } from '@/hooks/useAuth'
import { useCurrentUser } from '@/hooks/useCurrentUser'
import { useHashRoute } from '@/hooks/useHashRoute'
import Chat from '@/pages/Chat'
import Admin from '@/pages/Admin'
import Statistics from '@/pages/Statistics'

export default function App() {
  if (config.auth.kind === 'stub') return <StubApp />
  return <EntraApp apiScope={config.auth.apiScope} />
}

function StubApp() {
  const auth = useStubAuth()
  return <AuthedApp auth={auth} />
}

function EntraApp({ apiScope }: { apiScope: string }) {
  const auth = useEntraAuth(apiScope)
  return <AuthedApp auth={auth} />
}

/** Resolves the caller's role once and routes between chat and the
 * access-gated admin panel. The admin route is gated here for UX, but the
 * panel's endpoints are enforced server-side regardless. */
function AuthedApp({ auth }: { auth: AuthState }) {
  const route = useHashRoute()
  const acquireToken = useCallback(async () => {
    if (auth.kind !== 'authed') throw new Error('Not signed in')
    return auth.acquireToken()
  }, [auth])

  const currentUser = useCurrentUser({
    baseUrl: config.apiBaseUrl,
    acquireToken,
    enabled: auth.kind === 'authed',
  })
  const isAdmin = currentUser.kind === 'ready' && currentUser.isAdmin

  if (
    route.path === 'admin' &&
    auth.kind === 'authed' &&
    currentUser.kind === 'ready' &&
    currentUser.isAdmin
  ) {
    return <Admin auth={auth} currentOid={currentUser.oid} onNavigate={route.navigate} />
  }

  if (
    route.path === 'stats' &&
    auth.kind === 'authed' &&
    currentUser.kind === 'ready' &&
    currentUser.isAdmin
  ) {
    return <Statistics auth={auth} onNavigate={route.navigate} />
  }

  return <Chat auth={auth} isAdmin={isAdmin} onNavigate={route.navigate} />
}
